import { OpenInNew } from '@mui/icons-material';
import { TableBody, TableHead, TableRow, Tooltip, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { DomainSourcingOpportunity } from '@/api/generatedApi';
import { StyledHeaderCell, StyledTable, StyledTableCell, StyledTableRow } from './NsnTable';

const CONFIDENCE_BARS = 5;
const BAR_WIDTH_PX = 6;
const BAR_HEIGHT_PX = 14;
const BAR_GAP_PX = 3;

interface SourcingOpportunitiesTableProperties {
  opportunities: DomainSourcingOpportunity[];
}

const SupplierHeaderCell = styled(StyledHeaderCell)(() => ({
  width: '50%',
  textAlign: 'left',
  paddingLeft: '8px',
}));

const PartNumberHeaderCell = styled(StyledHeaderCell)(() => ({
  width: '140px',
}));

const PriceHeaderCell = styled(StyledHeaderCell)(() => ({
  minWidth: '150px',
  width: '150px',
  whiteSpace: 'nowrap',
}));

const ConfidenceHeaderCell = styled(StyledHeaderCell)(() => ({
  width: '120px',
  textAlign: 'center',
}));

const SupplierCell = styled(StyledTableCell)(() => ({
  fontWeight: 500,
  width: '50%',
  textAlign: 'left',
  paddingLeft: '8px',
  verticalAlign: 'top',
}));

const PartNumberCell = styled(StyledTableCell)(() => ({
  width: '140px',
  whiteSpace: 'nowrap',
}));

const PriceCell = styled(StyledTableCell)(() => ({
  minWidth: '150px',
  width: '150px',
}));

const ConfidenceCell = styled(StyledTableCell)(() => ({
  width: '120px',
  textAlign: 'center',
}));

const SupplierNameContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
}));

const SupplierNameRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const ReasoningSubtext = styled(Box)(({ theme }) => ({
  fontSize: '11px',
  lineHeight: '1.4',
  color: theme.palette.text.secondary,
  marginTop: '4px',
}));

const LinkIcon = styled(OpenInNew)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.primary.main,
  marginLeft: '6px',
  verticalAlign: 'middle',
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.primary.dark,
  },
}));

const SupplierLink = styled('a')(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: 'none',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const ConfidenceIndicator = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: BAR_GAP_PX,
});

const ConfidenceBar = styled(Box)<{ filled: boolean }>(({ theme, filled }) => ({
  width: BAR_WIDTH_PX,
  height: BAR_HEIGHT_PX,
  borderRadius: 2,
  backgroundColor: filled ? theme.palette.success.main : theme.palette.divider,
  transition: 'background-color 0.2s ease',
}));

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  textAlign: 'center',
  color: theme.palette.text.secondary,
  fontSize: '14px',
  backgroundColor: theme.palette.background.default,
  borderRadius: '4px',
  border: `1px solid ${theme.palette.divider}`,
}));

const RenderConfidence = ({ value }: { value: number }) => {
  const numberOfConfidenceBarsToBeFilled = Math.ceil((value / 100) * CONFIDENCE_BARS);

  return (
    <Tooltip title={`${Math.round(value)}% confidence`}>
      <ConfidenceIndicator>
        {Array.from({ length: CONFIDENCE_BARS }, (_, index) => (
          <ConfidenceBar key={index} filled={index < numberOfConfidenceBarsToBeFilled} />
        ))}
      </ConfidenceIndicator>
    </Tooltip>
  );
};

export const formatPrice = (price: number | string): string => `$${Number(price).toFixed(2)}`;

const SupplierLinkWithIcon = ({
  url,
  children,
  tooltipText = 'Open in new tab',
}: {
  url: string;
  children: React.ReactNode;
  tooltipText?: string;
}) => (
  <SupplierNameRow>
    {children && (
      <SupplierLink href={url} target='_blank' rel='noopener noreferrer'>
        {children}
      </SupplierLink>
    )}
    <Tooltip title={tooltipText}>
      <Box component='a' href={url} target='_blank' rel='noopener noreferrer' sx={{ lineHeight: 0 }}>
        <LinkIcon />
      </Box>
    </Tooltip>
  </SupplierNameRow>
);

const PriceAvailabilityCell = ({ price, availability }: { price?: number | null; availability?: string }) => {
  if (!price && !availability) {
    return <PriceCell>—</PriceCell>;
  }

  return (
    <PriceCell>
      {price && <Box sx={{ fontSize: '12px', fontWeight: 500 }}>{formatPrice(price)}</Box>}
      {availability && (
        <Box sx={theme => ({ fontSize: '11px', color: theme.palette.text.secondary, marginTop: '2px' })}>
          {availability}
        </Box>
      )}
    </PriceCell>
  );
};

export const SourcingOpportunitiesTable = ({ opportunities }: SourcingOpportunitiesTableProperties) => {
  if (opportunities.length === 0) {
    return (
      <EmptyStateContainer>No sourcing opportunities were found during this report generation.</EmptyStateContainer>
    );
  }

  return (
    <StyledTable size='small'>
      <TableHead>
        <TableRow>
          <SupplierHeaderCell>Supplier</SupplierHeaderCell>
          <PartNumberHeaderCell>Part Number</PartNumberHeaderCell>
          <PriceHeaderCell>Price / Availability</PriceHeaderCell>
          <ConfidenceHeaderCell>Confidence</ConfidenceHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {opportunities.map(opportunity => (
          <StyledTableRow key={opportunity.id}>
            <SupplierCell>
              <SupplierNameContainer>
                <SupplierLinkWithIcon url={opportunity.url}>{opportunity.supplier}</SupplierLinkWithIcon>
                <ReasoningSubtext>{opportunity.reasoning}</ReasoningSubtext>
              </SupplierNameContainer>
            </SupplierCell>
            <PartNumberCell>{opportunity.partNumber ?? '—'}</PartNumberCell>
            <PriceAvailabilityCell price={opportunity.price} availability={opportunity.availability} />
            <ConfidenceCell>
              <RenderConfidence value={opportunity.confidenceScore} />
            </ConfidenceCell>
          </StyledTableRow>
        ))}
      </TableBody>
    </StyledTable>
  );
};
